import type { SipDecreaseRecommendation } from '../../../../api/sipPlan.types'
import { SipCategoryBucketKey } from '../../../../api/types'
import {
  PlansFundCard,
  type PlanFundCardCta
} from '../../../../../lumpsum/plans-list/lumpsum-ui-components/PlansFundCard'
import { useDecreaseSipAction } from '../../../../../execution/sip/decrease/usePlanDecreaseSipAction'
import { showAsFormattedRupeeAmount } from '../../../../../display-values/lumpsumPlanAmount'
import { formatTargetSip } from '../../../../../display-values/sipPlanAmount'
import { useOpenSipPlanRecommendationBottomSheet } from '../../../../plan-bottom-sheet/open/useOpenSipPlanRecommendationBottomSheet'
import { useSipPlansContext } from '../../../sipPlansContext'
import { PlansFundRole, PlansScreen } from '../../../../../analytics/plansEvents'
import { trackPlansFundCardCta, trackPlansFundCardOpen } from '../../../../../analytics/plansTrack'
import {
  SipAmountBadge,
  SipFundCardTopRightLabel
} from './sipYourPlanFundCardShared'

type DecreaseSipFundCardProps = {
  recommendation: SipDecreaseRecommendation
  showBottomDivider?: boolean
}

export function DecreaseSipFundCard({
  recommendation,
  showBottomDivider = true
}: DecreaseSipFundCardProps) {
  const { plansOverview } = useSipPlansContext()
  const { openSipPlanRecommendationBottomSheet } = useOpenSipPlanRecommendationBottomSheet()
  const { onDecreaseSip } = useDecreaseSipAction()
  const totalIdealSipAmount = plansOverview?.totalIdealSipAmount ?? 0
  const isin = recommendation?.isin ?? ''
  const fundName = recommendation?.fundName ?? ''
  const screenName = PlansScreen.SipYourPlan
  const fundRole = PlansFundRole.DecreaseSip
  const currentSipAmount = recommendation?.currentSipAmount ?? 0
  const recommendedSipAmount = recommendation?.recommendedSipAmount ?? 0
  const formattedTargetSip = formatTargetSip({ rawAmount: recommendedSipAmount })
  const formattedCurrentSip = showAsFormattedRupeeAmount({ rawAmount: currentSipAmount })

  const openPlanBottomSheet = () => {
    openSipPlanRecommendationBottomSheet({
      isin,
      totalIdealSipAmount,
      bucketType: SipCategoryBucketKey.decreaseRecommendations,
      recommendation,
      manageSipCta: plansOverview?.manageSipCta
    })
  }

  const handleCardClick = () => {
    trackPlansFundCardOpen({ screenName, fundRole, fundName, fundIsin: isin })
    openPlanBottomSheet()
  }

  const handleEditSip = () => {
    trackPlansFundCardCta({ screenName, fundRole, buttonLabel: 'Edit SIP', fundName, fundIsin: isin })
    onDecreaseSip(recommendation, plansOverview?.manageSipCta)
  }

  const ctas: PlanFundCardCta[] = [
    {
      id: `${isin}-edit-sip`,
      label: 'Edit SIP',
      onClick: handleEditSip,
      variant: 'tertiary'
    },
    {
      id: `${isin}-learn-more`,
      label: 'Learn more',
      onClick: () => {
        trackPlansFundCardCta({ screenName, fundRole, buttonLabel: 'Learn more', fundName, fundIsin: isin })
        openPlanBottomSheet()
      },
      variant: 'tertiary'
    }
  ]

  return (
    <PlansFundCard
      fund={{
        isin,
        logoUrl: recommendation?.mfLogoUrl ?? '',
        title: fundName,
        fundNature: recommendation?.fundNature ?? '',
        category: recommendation?.fundCategory ?? '',
        purchaseMode: recommendation?.purchaseMode ?? ''
      }}
      topRight={<SipFundCardTopRightLabel label="Reduce to" targetSipAmount={formattedTargetSip} />}
      badgeRowExtras={currentSipAmount > 0 ? <SipAmountBadge currentSipAmount={formattedCurrentSip} /> : null}
      status={{
        rank: recommendation?.powerRank ?? 0,
        rating: recommendation?.powerRating ?? ''
      }}
      recommendationStatus={recommendation?.recommendationStatus}
      recommendationStatusDisplayText={recommendation?.recommendationStatusDisplayText}
      ctas={ctas}
      showBottomDivider={showBottomDivider}
      onCardClick={handleCardClick}
    />
  )
}
